import type { ComponentChild, RefObject, JSX } from 'preact';
import { ForwardedRef, forwardRef } from 'preact/compat';
import { Offset, WindowOverlay, WindowOverlayPlacement } from '../UNSAFE_WindowOverlay';
import { TestIdProps, useTestId } from '../hooks/UNSAFE_useTestId';
import { useAnimation } from '../hooks/UNSAFE_useAnimation';
import { useEffect, useRef, useCallback, useMemo, useState } from 'preact/hooks';
import { mergeRefs } from '../utils/PRIVATE_refUtils';

import { DialogRedwoodTheme } from './themes/redwood/DialogTheme';
import { DialogStyles } from './themes/DialogStyles.css';
import { FocusTrap } from '../UNSAFE_FocusTrap';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { Layer } from '../UNSAFE_Layer';
import { Modal } from '../UNSAFE_Modal';
import { LogicalParentRef, LogicalParentRefContext } from '../UNSAFE_Layer/LogicalParentRefContext';
import { classNames } from '../utils/UNSAFE_classNames';
import { globalImages } from '#/ImageVars.css';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useId } from '../hooks/UNSAFE_useId';
import { dimensionInterpolations, DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { useResizeObserver } from '../hooks/UNSAFE_useResizeObserver';

import { DraggableProps, useDraggable } from '../hooks/PRIVATE_useDraggable';
import { Floating, Coords } from '../UNSAFE_Floating';
import { IconButton } from '../UNSAFE_IconButton';
import { CloseIcon } from '../PRIVATE_ThemedIcons/CloseIcon';
import { Resizable, ResizableConstraints } from '../PRIVATE_Resizable/Resizable';
import { Position, Rect } from '../utils/UNSAFE_flexibleComponentUtils';
import { useBreakpoints } from '../hooks/UNSAFE_useBreakpoints';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';
import {
  SLIDE_DOWN_XLARGE,
  SLIDE_UP_XLARGE,
  DURATION_LARGE
} from '../utils/PRIVATE_animationUtils';
import {
  allTabbableElements,
  focusOn,
  focusWithin,
  getActiveElement
} from '../utils/PRIVATE_tabbableUtils';

import type { UnsafeDomHandle } from '#utils/UNSAFE_unsafeDomAccess';
import { UNSAFE_DOM_ACCESS } from '#utils/UNSAFE_unsafeDomAccess';

// dialog states
type Status = 'initial' | 'opening' | 'closing' | 'unmounted';
type VisibilityStatus = 'hidden' | 'visible';
type Animations = 'zoomIn' | 'zoomOut' | 'slideUp' | 'slideDown';

type IntrinsicProps = Pick<UnpackSignals<JSX.IntrinsicElements['div']>, 'children' | 'role'>;

type OnCloseDetail = {
  /**
   * reason for closing the dialog
   */
  reason: 'escapeKey' | 'icon';
};

export type CancelBehavior = 'none' | 'escape' | 'icon';

type DialogProps = IntrinsicProps &
  DimensionProps & {
    /**
     * Optional ref for an element to focus on when component mounts
     */
    autoFocusRef?: RefObject<{ focus: () => void }>;

    /**
     * On Close callback.
     */
    onClose?: (detail: OnCloseDetail) => void;

    /**
     * On opening or closing transition end callback.
     */
    onTransitionEnd?: (value: boolean) => void;

    /**
     * Determines whether the Dialog is open.
     */
    isOpen: boolean;

    /**
     * Dialog header content. Can be a string or VNode (Component Child).
     * <p>
     * When a plain string is passed, it will be used as the dialog title. Otherwise, the prop value
     * will be used as the header content.
     * </p>
     * <p>
     * Dialog will automatically refer to the header content as the accessible label.
     * The user can override this behavior by specifying the <code>aria-labelledby</code> property
     * and setting its value as needed.
     * </p>
     */
    header?: ComponentChild;

    /**
     * Dialog footer content.
     */
    footer?: ComponentChild;

    /**
     * Determines dialog modality. Default is 'modal'.
     */
    modality?: 'modal' | 'modeless';

    /**
     * Specifies dialog's cancel behavior. Default is 'none'.
     */
    cancelBehavior?: CancelBehavior;

    /**
     * Specifies dialog's launcher. After dialog closes, it returns focus to the launcher.
     * If not specified, focus goes back to the last active element before dialog opened.
     */
    launcherRef?: RefObject<HTMLElement | UnsafeDomHandle<HTMLElement>>;

    /**
     * Specifies dialog's anchor. Dialog is placed relative to its anchor. If not specified,
     * it is placed relatively to window.
     */
    anchorRef?: RefObject<Element | Coords>;

    /**
     * Determines placement of the dialog relative to the viewport.
     * Supported values are:
     * <p> <code>center</code>, <code>top</code>, <code>top-start</code>, <code>top-end</code>,
     * <code>end</code>, <code>end-top</code>, <code>end-bottom</code>,
     * <code>bottom</code>, <code>bottom-start</code>, <code>bottom-end</code>,
     * <code>start</code>, <code>start-top</code>, <code>start-bottom</code>
     * </p>
     * Default is <code>center</code>.
     */
    placement?: WindowOverlayPlacement;

    /**
     * Determines the offset of the Dialog from its core placement along the specified axes.
     *
     * Offset could be a number or an object
     *
     * An object has the following type:
     * <pre>
     *   {
     *     mainAxis?: number;
     *     crossAxis?: number;
     *   }
     * </pre>
     * For the <code>center</code> placement the convention for the offset type is that mainAxis defines
     * offset from the top, crossAxis defines offset from the left side of the view port.
     *
     * A number is a shortcut for setting the mainAxis.
     */
    offset?: Offset;

    /**
     * Determines if the Dialog should be sensitive to drag gestures. To create a draggable dialog,
     * set <code>dragAffordance='header'</code> and specify the <code>onDragMove</code> handler.
     */
    dragAffordance?: 'none' | 'header';

    /**
     * Determines if the Dialog should support resizing. To create a resizable dialog,
     * set <code>resizeBehavior='resizable'</code> and specify the <code>onResize</code> handler.
     */
    resizeBehavior?: 'none' | 'resizable';

    /**
     * Callback to be invoked if <code>dragAffordance</code> is set to <code>header</code> and a drag gesture is initiated.
     * The current position is reported in the <code>pos</code> parameter.
     *
     * @param pos
     */
    onDragStart?: (pos: Position) => void;

    /**
     * Callback to be invoked if <code>dragAffordance</code> is set to <code>header</code> and a drag gesture is detected.
     * The current position is reported in the <code>pos</code> parameter.
     *
     * @param pos
     */
    onDragMove?: (pos: Position) => void;

    /**
     * Callback to be invoked if <code>dragAffordance</code> is set to <code>header</code> and a drag gesture is completed.
     * The current position is reported in the <code>pos</code> parameter.
     *
     * @param pos
     */
    onDragEnd?: (pos: Position) => void;

    /**
     * Callback to be invoked if <code>resizeBehavior</code> is set to <code>resizable</code> and
     * a resize gesture is initiated. The current position and dimensions are reported in the
     * <code>rect</code> parameter.
     *
     * @param rect
     */
    onResizeStart?: (rect: Rect) => void;

    /**
     * Callback to be invoked if <code>resizeBehavior</code> is set to <code>resizable</code> and
     * a resize gesture is detected. The current position and dimensions are reported in the
     * <code>rect</code> parameter.
     *
     * @param rect
     */
    onResize?: (rect: Rect) => void;

    /**
     * Callback to be invoked if <code>resizeBehavior</code> is set to <code>resizable</code> and
     * a resize gesture is completed. The current position and dimensions are reported in the
     * <code>rect</code> parameter.
     *
     * @param rect
     */
    onResizeEnd?: (rect: Rect) => void;

    /**
     * Specifies callback triggered when initial focus is set or when a modeless dialog receives focus
     * via the F6 key.
     */
    onFocusSet?: () => void;

    /**
     * Determines if the header decoration should be displayed. The default is <code>on</code>
     * which renders a textured strip at the top of the dialog header in the Redwood theme.
     * If set to <code>off</code>, no decoration is rendered.
     */
    headerDecoration?: 'off' | 'on';

    /**
     * Specifies the ARIA role type. Depending on how the dialog is used in the page, the developer
     * should choose from the following:
     * <ul>
     *   <li><code>dialog</code> defines an application window that is designed to interrupt the current
     *      processing of an application in order to prompt the user to enter information or require
     *      a response.</li>
     *   <li><code>alertdialog</code> defines type of dialog that contains an alert message, where initial
     *      focus goes to an element within the dialog.</li>
     * </ul>
     */
    role?: string;

    /**
     * Optional <code>aria-labelledby</code> property referring to the content to be used as Dialog's
     * accessible <label htmlFor="" className=""></label>
     * If not specified, the Dialog will automatically refer to the entire header content as the accessible label.
     */
    'aria-labelledby'?: string;

    /**
     * Optional accessible description for the dialog.
     */
    'aria-describedby'?: string;
  } & TestIdProps;

// dialog header
type DialogHeaderProps = Pick<DialogProps, 'children'> & {
  id?: string;
  closeButtonRef?: RefObject<HTMLDivElement>;
  cancelBehavior?: CancelBehavior;
  onClose?: (detail: OnCloseDetail) => void;
  draggableProps?: DraggableProps;
  headerDecoration?: 'off' | 'on';
};

const StyledDialogHeader = ({
  children,
  id,
  closeButtonRef,
  cancelBehavior,
  onClose,
  draggableProps,
  headerDecoration = 'on'
}: DialogHeaderProps) => {
  const { styles } = useComponentTheme<never, DialogStyles>(DialogRedwoodTheme);

  const isSmallWidth = !useBreakpoints()['md'];

  const classes = classNames([
    styles.dialogHeaderStyle,
    isSmallWidth ? styles.dialogHeaderStyleSmall : styles.dialogHeaderStyleDesktop,
    ...(headerDecoration === 'on' ? [styles.dialogHeaderDecorationStyle] : [])
  ]);

  const content: ComponentChild =
    typeof children === 'string' ? (
      <h1 id={id} className={styles.dialogTitleStyle}>
        {children}
      </h1>
    ) : (
      <>{children}</>
    );

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const handleIconClose = () => {
    onClose?.({ reason: 'icon' });
  };

  const closeIcon: ComponentChild =
    cancelBehavior === 'icon' ? (
      <div>
        <div className={styles.dialogHeaderCloseIconStyle} ref={closeButtonRef}>
          <IconButton
            aria-label={translations.message_close()}
            size="sm"
            variant="ghost"
            onAction={handleIconClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>
    ) : null;

  return (
    <div className={classes} {...draggableProps}>
      {content}
      {closeIcon}
    </div>
  );
};

// dialog footer

type DialogFooterProps = Pick<DialogProps, 'children'> & {
  style?: string;
};

const StyledDialogFooter = forwardRef(
  ({ children, style }: DialogFooterProps, ref: ForwardedRef<HTMLDivElement | null>) => {
    return (
      <div ref={ref} className={style}>
        {children}
      </div>
    );
  }
);

// styled dialog

type MainProps = Pick<DialogProps, 'children' | 'header' | 'footer' | 'role'> &
  Pick<UnpackSignals<JSX.IntrinsicElements['div']>, 'style' | 'onKeyDown'> & {
    visibility: VisibilityStatus;
    labelId?: string;
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
    cancelBehavior?: CancelBehavior;
    draggableProps?: DraggableProps;
    isResizable?: boolean;
    headerDecoration?: 'off' | 'on';
    containerRef?: RefObject<HTMLElement>;
    onClose?: (detail: OnCloseDetail) => void;
    onResizeStart?: (rect: Rect) => void;
    onResize?: (rect: Rect) => void;
    onResizeEnd?: (rect: Rect) => void;
    closeButtonRef?: RefObject<HTMLDivElement>;
    bodyRef?: RefObject<HTMLDivElement>;
    footerRef?: RefObject<HTMLDivElement | null>;
  } & TestIdProps;

const StyledDialog = forwardRef(
  (
    {
      children,
      header,
      footer,
      visibility,
      labelId,
      role,
      ariaLabelledBy,
      ariaDescribedBy,
      cancelBehavior,
      draggableProps,
      isResizable,
      headerDecoration,
      containerRef,
      onClose,
      onResizeStart,
      onResize,
      onResizeEnd,
      onKeyDown,
      testId,
      style,
      closeButtonRef,
      bodyRef,
      footerRef
    }: MainProps,
    ref: ForwardedRef<HTMLDivElement | null>
  ) => {
    const elementRef = useRef<HTMLDivElement>(null);
    const mergedRef = mergeRefs(ref, elementRef);

    // computed dialog dimension limits
    const sizeConstraints = useRef<ResizableConstraints>();

    const [isSmallHeight, setSmallHeight] = useState<boolean>(false);
    const isSmallWidth = !useBreakpoints()['md'];

    const { classes: styleClasses, styles } = useComponentTheme<never, DialogStyles>(
      DialogRedwoodTheme
    );

    const resizeHandler = useCallback(() => {
      if (elementRef.current) {
        if (elementRef.current.clientHeight < 400) {
          setSmallHeight(true);
        } else {
          setSmallHeight(false);
        }
        if (elementRef.current) {
          const cs = window.getComputedStyle(elementRef.current);
          sizeConstraints.current = {
            minHeight: parseFloat(cs.minHeight),
            maxHeight: parseFloat(cs.maxHeight),
            minWidth: parseFloat(cs.minWidth),
            maxWidth: parseFloat(cs.maxWidth)
          };
        }
      }
    }, []);

    // set initial Height
    useEffect(() => {
      resizeHandler();
    }, [resizeHandler]);

    useResizeObserver(elementRef, resizeHandler);

    // for small widths (mobile), use the 'smallWidth' dialog style
    // otherwise, check for small height to apply 'smallWidth' or 'desktop' styles accordingly
    const baseStyle = isSmallWidth ? styles.baseStyleSmallWidth : styles.baseStyleDesktop;

    // content should scroll depending on the viewport height
    // on large screens, the header and footer should be "sticky",
    // at small viewport heights, the entire content should be scrollable
    const contentStyle = isSmallHeight
      ? styles.dialogContentStyleNoShrink
      : styles.dialogContentStyle;

    const bodyStyle = classNames([
      styles.dialogBodyStyle,
      isSmallWidth ? styles.dialogBodyStyleSmall : styles.dialogBodyStyleDesktop
    ]);

    const footerStyle = classNames([
      styles.dialogFooterStyle,
      isSmallWidth ? styles.dialogFooterStyleSmall : styles.dialogFooterStyleDesktop
    ]);

    const visibilityStyle =
      visibility === 'visible' ? styles.visibilityVisible : styles.visibilityHidden;

    const testIdProps = useTestId(testId);

    return (
      <FocusTrap restoreFocusRef={false}>
        <div
          ref={mergedRef}
          tabIndex={-1}
          className={classNames([styleClasses, baseStyle, visibilityStyle, globalImages])}
          style={style}
          onKeyDown={onKeyDown}
          role={role}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          {...testIdProps}>
          <div className={styles.dialogContainerStyle}>
            <StyledDialogHeader
              id={labelId}
              onClose={onClose}
              headerDecoration={headerDecoration}
              cancelBehavior={cancelBehavior}
              closeButtonRef={closeButtonRef}
              draggableProps={draggableProps}>
              {header}
            </StyledDialogHeader>
            <div className={contentStyle} ref={bodyRef}>
              {children ? <div className={bodyStyle}>{children}</div> : null}
            </div>
            <StyledDialogFooter ref={footerRef} style={footerStyle}>
              {footer}
            </StyledDialogFooter>
          </div>
          {isResizable && (
            <Resizable
              onResizeStart={onResizeStart}
              onResize={onResize}
              onResizeEnd={onResizeEnd}
              containerRef={containerRef}
              constraints={sizeConstraints.current}></Resizable>
          )}
        </div>
      </FocusTrap>
    );
  }
);

// main Dialog component

/**
 * WAI-ARIA-compliant dialog component. A dialog is a floating window that typically contains
 * a title bar and a content area.
 * The dialog is generally placed on top of the rest of the page content. It can be either modal
 * (only the content in the dialog can be interacted with) or non-modal (it's still possible
 * to interact with content outside of the dialog).
 */
export const Dialog = forwardRef(
  (
    {
      children,
      isOpen,
      header = '',
      footer,
      modality = 'modal',
      launcherRef,
      anchorRef,
      autoFocusRef,
      placement,
      offset = 0,
      cancelBehavior = 'none',
      dragAffordance = 'none',
      resizeBehavior = 'none',
      headerDecoration = 'on',
      onDragStart,
      onDragMove,
      onDragEnd,
      onResizeStart,
      onResize,
      onResizeEnd,
      onFocusSet,
      onClose,
      onTransitionEnd,
      role = 'dialog',
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      testId,
      ...props
    }: DialogProps,
    ref: ForwardedRef<HTMLDivElement | null>
  ) => {
    const [status, _setStatus] = useState<Status>(isOpen ? 'initial' : 'unmounted');
    const _status = useRef<Status>(isOpen ? 'initial' : 'unmounted');
    const setStatus = (s: Status) => {
      _status.current = s;
      _setStatus(s);
    };

    const [visibility, setVisibility] = useState<VisibilityStatus>('hidden');

    const prevStatusRef = useRef<Status>('unmounted');

    const floatingRef = useRef<HTMLDivElement>(null);
    const contentWrapperRef = useRef<HTMLDivElement | null>(null);
    const localLauncherRef = useRef<HTMLElement | null>(null);
    const closeButtonRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const footerRef = useRef<HTMLDivElement | null>(null);

    const isSmallWidth = !useBreakpoints()['md'];
    const derivedPlacement = isSmallWidth ? 'bottom' : placement ? placement : 'center';
    const animationType = isSmallWidth
      ? status === 'opening'
        ? 'slideUp'
        : 'slideDown'
      : status === 'opening'
      ? 'zoomIn'
      : 'zoomOut';

    const uniqueId = useId();
    const labelId = ariaLabelledBy ? ariaLabelledBy : uniqueId;

    const interpolations = [...Object.values(dimensionInterpolations)];
    const styleInterpolations = mergeInterpolations<DimensionProps>(interpolations);
    const { ...styles } = styleInterpolations(props);
    const shouldReturnFocus = useRef<boolean>(false);

    const setInitialFocus = useCallback(() => {
      // Initial focus policy doc:
      // The first :tabbable element within the dialog body
      // The first :tabbable element within the dialog footer
      // The dialog's close button
      // The dialog itself
      if (autoFocusRef && autoFocusRef.current) {
        focusOn(autoFocusRef.current);
      } else if (bodyRef.current && allTabbableElements(bodyRef.current).length > 0) {
        // The first :tabbable element within the dialog body
        focusWithin(bodyRef.current);
      } else if (footerRef.current && allTabbableElements(footerRef.current).length > 0) {
        // The first :tabbable element within the dialog footer
        focusWithin(footerRef.current);
      } else if (closeButtonRef.current && allTabbableElements(closeButtonRef.current).length > 0) {
        // The dialog's close button
        focusWithin(closeButtonRef.current);
      } else {
        // The dialog itself
        focusWithin(contentWrapperRef.current as HTMLElement);
      }
      onFocusSet?.();
    }, [autoFocusRef, onFocusSet]);

    const returnFocus = useCallback((): void => {
      const launcherEl = localLauncherRef.current;

      if (shouldReturnFocus.current && launcherEl) {
        focusOn(launcherEl);
        shouldReturnFocus.current = false;
      }
    }, []);

    // Animation
    const { nodeRef } = useAnimation<Animations, HTMLDivElement>(animationType, {
      animationStates,
      isAnimatedOnMount: true,
      onAnimationEnd: () => {
        if (isOpen) {
          onTransitionEnd?.(true);
        } else if (!isOpen) {
          // Should return focus?
          // Modeless: check whether Dialog contains focus before it unmounts
          // Modal: always return focus
          shouldReturnFocus.current =
            contentWrapperRef.current?.contains(getActiveElement() as HTMLElement) || false;
          nodeRef(null);
          setVisibility('hidden');
          setStatus('unmounted');
          onTransitionEnd?.(false);
        }
      }
    });

    const mergedRef = useMemo(() => mergeRefs(contentWrapperRef, ref), [contentWrapperRef, ref]);

    const launcherKeyDownCallback = useCallback(
      (event: KeyboardEvent): void => {
        if (localLauncherRef.current === getActiveElement() && event.code === 'F6') {
          // Prevent default F6 handlers.
          // F6 is a standard Chrome address bar shortcut on Windows.
          event.preventDefault();
          event.stopPropagation();

          focusWithin(contentWrapperRef.current as HTMLElement);
          onFocusSet?.();
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [modality]
    );

    const { draggableProps } = useDraggable({
      isDisabled: dragAffordance === 'none',
      containerRef: floatingRef,
      onDragStart,
      onDragMove,
      onDragEnd
    });

    useEffect(() => {
      // 1. Ignore status update on initial render or return focus on component unmount
      if (status === 'unmounted' && !isOpen) {
        if (status !== prevStatusRef.current) {
          returnFocus();
        }
      }

      // 2. Mount visually hidden component
      // Component gets mounted, but will be visually hidden to avoid flickering
      // Flickering happens because it gets rendered before animation hook repositions
      // it in next render cycle
      else if (status === 'unmounted' && isOpen) {
        setStatus('initial');
      }

      // 3. Setup animation
      // Component has been mounted.
      // We will kick out animation as node is already present in DOM.
      // To avoid flickering, we still keep it visually hidden until the next render cycle.
      else if (status === 'initial' && isOpen) {
        nodeRef(contentWrapperRef.current);
        setStatus('opening');
      }

      // 3. Unhide component. It becomes visible.
      else if (status === 'opening' && isOpen) {
        setVisibility('visible');
      }

      // 4. Closing
      else if (!isOpen && _status.current !== 'unmounted') {
        setStatus('closing');
      }

      return () => {
        // remember the previous status
        prevStatusRef.current = status;
      };
    }, [isOpen, status, nodeRef, returnFocus]);

    // Destroy case handler
    useEffect(() => {
      return () => {
        const launcherEl = localLauncherRef.current;
        if (
          // eslint-disable-next-line react-hooks/exhaustive-deps
          (contentWrapperRef.current?.contains(getActiveElement() as HTMLElement) || false) &&
          launcherEl
        ) {
          // In destroy case ModalManager is blocking returning of the focus.
          // We need to reschedule it to workaround that
          setTimeout(() => {
            focusOn(launcherEl);
          }, 0);
        }
      };
    }, []);

    // Initialize launcher
    useEffect(() => {
      if (isOpen) {
        localLauncherRef.current = _getLauncherElement(launcherRef);

        // Register F6 key handler to enter a modeless dialog
        if (modality === 'modeless') {
          localLauncherRef.current?.addEventListener('keydown', launcherKeyDownCallback);
        }
      }
      return () => {
        // Deregister F6 key handler
        if (modality === 'modeless') {
          localLauncherRef.current?.removeEventListener('keydown', launcherKeyDownCallback);
        }
      };
    }, [isOpen, launcherRef, launcherKeyDownCallback, modality]);

    // setInitialFocus when the dialog becomes visible
    useEffect(() => {
      if (visibility === 'visible') {
        setInitialFocus();
      }
    }, [visibility, setInitialFocus]);

    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.code) {
        case 'Escape':
          onClose?.({ reason: 'escapeKey' });
          break;
        case 'F6':
          // Focus launcher
          if (localLauncherRef.current && modality === 'modeless') {
            focusOn(localLauncherRef.current);
          }
          // Prevent default F6 handlers. F6 is a standard Chrome address bar shortcut on Windows.
          event.preventDefault();
          event.stopPropagation();
          break;
      }
    };

    const handleBackdropClick = () => {
      // in case of a backdrop click, move focus to dialog's root element
      contentWrapperRef.current?.focus();
    };

    const renderDialogContent = () => {
      return (
        <StyledDialog
          ref={mergedRef}
          labelId={labelId}
          header={header}
          footer={footer}
          role={role}
          ariaLabelledBy={labelId}
          ariaDescribedBy={ariaDescribedBy}
          onKeyDown={handleKeyDown}
          testId={testId}
          style={styles}
          visibility={visibility}
          cancelBehavior={cancelBehavior}
          draggableProps={draggableProps}
          isResizable={resizeBehavior === 'resizable'}
          headerDecoration={headerDecoration}
          containerRef={floatingRef}
          onClose={onClose}
          onResizeStart={onResizeStart}
          onResize={onResize}
          onResizeEnd={onResizeEnd}
          closeButtonRef={closeButtonRef}
          bodyRef={bodyRef}
          footerRef={footerRef}>
          {children}
        </StyledDialog>
      );
    };

    const renderDialog = () => {
      if (anchorRef && anchorRef.current) {
        return (
          <Floating
            ref={floatingRef}
            anchorRef={anchorRef}
            placement={'bottom-start'}
            flipOptions={{ mainAxis: false, crossAxis: false }}
            shiftOptions={{ mainAxis: false, crossAxis: false }}>
            {renderDialogContent()}
          </Floating>
        );
      } else {
        return (
          <WindowOverlay placement={derivedPlacement} offset={offset}>
            {renderDialogContent()}
          </WindowOverlay>
        );
      }
    };

    const logicalLauncherRef = launcherRef as LogicalParentRef;

    if (modality === 'modal') {
      return (
        <LogicalParentRefContext.Provider value={logicalLauncherRef}>
          <Modal isOpen={status !== 'unmounted'} onBackdropClick={handleBackdropClick}>
            {renderDialog()}
          </Modal>
        </LogicalParentRefContext.Provider>
      );
    } else {
      return status !== 'unmounted' ? (
        <Layer logicalParentRef={logicalLauncherRef}>{renderDialog()}</Layer>
      ) : null;
    }
  }
);

function _getLauncherElement(launcherRef: DialogProps['launcherRef']) {
  const launcher = launcherRef?.current;

  if (launcher) {
    const launcherElement = launcher instanceof Element ? launcher : launcher[UNSAFE_DOM_ACCESS];

    if (document.body.contains(launcherElement)) {
      return launcherElement;
    }
  }

  return getActiveElement() as HTMLElement;
}

const animationStates = {
  zoomIn: {
    from: {
      scaleX: 0.9,
      scaleY: 0.9,
      transformOrigin: 'center',
      opacity: 0
    },
    to: {
      scaleX: 1,
      scaleY: 1,
      opacity: 1
    },
    options: {
      duration: 200
    }
  },
  zoomOut: {
    from: {
      scaleX: 1,
      scaleY: 1,
      transformOrigin: 'center',
      opacity: 1
    },
    to: {
      scaleX: 0.9,
      scaleY: 0.9,
      opacity: 0
    },
    options: {
      duration: 200
    }
  },
  slideUp: {
    ...SLIDE_UP_XLARGE,
    options: {
      duration: DURATION_LARGE
    }
  },
  slideDown: {
    ...SLIDE_DOWN_XLARGE,
    options: {
      duration: DURATION_LARGE
    }
  }
};
